import jsonConfig from "./config.json";

interface SentryConfig {
  dsn: string;
  environment: string;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  unmask: string[];
}

interface BackendConfig {
  host?: string;
  path: string;
  ssl?: boolean;
}

interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
}

interface FeatureFlagsConfig {
  // GO Feature Flag relay endpoint. When omitted, feature flags fail closed
  // (every flag resolves to its default of `false`).
  endpoint?: string;
  // Environment dimension of the evaluation context. Must match the value the
  // website sends (e.g. "PRODUCTION") so the same user gets the same results.
  environment: string;
}

interface ApiConfig {
  // REST/Connect (Vanguard) API base URLs keyed by NGP region. Defaults point
  // at production. Keyed explicitly so a typo'd key is a compile error rather
  // than an undefined URL at runtime.
  endpoints: {
    EU: string;
    US: string;
  };
}

interface IntercomConfig {
  // Whether to boot the Intercom messenger at all. Off unless a config file
  // explicitly sets it, and deliberately absent from every config.json in this
  // repo — the panel has no support surface to put the chat on, so it is
  // disabled everywhere until it does. Customers reach support through the
  // website, which keeps its messenger on the support pages.
  //
  // Optional rather than a required `boolean` on purpose: the config is applied
  // with a type assertion (`<Config>jsonConfig` below), not parsed, and
  // tsconfig sets strictNullChecks:false — so declaring it required would
  // compile and type as `boolean` while actually being `undefined`. This says
  // what is true.
  //
  // To bring the messenger back: add `"enabled": true` to the intercom block of
  // the relevant config AND have the `support-chat` feature flag. app.vue checks
  // this key first and only then the flag, and explains why both are needed. The
  // boot path is intact, not removed.
  //
  // Note it is applied at BUILD time: CI generates config.json from
  // config.prod-tpl.json before building, so flipping it needs a rebuild, not a
  // configmap edit.
  enabled?: boolean;
  appId: string;
  // Intercom workspace region (NOT the API region) — "eu" for our workspace.
  // Narrowed to the SDK's accepted values so a wrong-case/whitespace value is a
  // compile error instead of a silent runtime failure.
  region: "us" | "eu" | "ap";
}

interface Config {
  sentry: SentryConfig | null | undefined;
  backend: BackendConfig;
  keycloak: KeycloakConfig;
  featureFlags: FeatureFlagsConfig;
  api: ApiConfig;
  intercom: IntercomConfig;
}

const config = <Config>jsonConfig;

function buildUri(scheme: string): string {
  const ssl = config.backend.ssl || window.location.protocol === "https:";
  const host = config.backend.host || window.location.host;
  const path = config.backend.path || "/";
  return `${scheme}${ssl ? "s" : ""}://${host}${path}`;
}

export const gqlUri = buildUri("http");
export const gqlWsUri = buildUri("ws");

export default config;
